/**
 * A code-drawn replica of the app's note-detail screen, shown in the hero.
 *
 * The page used to prove its claims with the raw markdown file
 * (`note-artifact.tsx`); the redesign proves them with the app instead, and the
 * frontmatter jargon goes. Every element here mirrors the real screen as
 * `mobile/app/recording-detail.tsx` renders it: the pill back button, the serif
 * title and transcript, the squared-corner tag chips, the "Transcription" row
 * with its Listen pill, "Linked Recordings" with its add control and an
 * unlinkable row, the Related card, and the play bar pinned to the bottom. If
 * the app's screen changes, this changes with it — a mock that drifts from the
 * product is worse than none.
 *
 * ## Keeping it honest
 *
 * It drifted once already, which is the reason for this paragraph. The mock was
 * drawn while `suggestionFloor` was `POSITIVE_INFINITY`, so the Related section
 * existed in the app's code but no score could clear the floor and it never
 * rendered on a device — the mock was accurate to ship a page without it. When
 * the floor moved to 0.25 the section went live and the mock stayed put, which
 * left the page's two largest sections arguing for self-linking beside a phone
 * that showed none of it. The lesson worth keeping: this file tracks what the
 * screen *renders*, not what its source contains.
 *
 * The one editorial liberty is the status bar: the airplane glyph is on,
 * because the capture pipeline — recording, transcription, linking — genuinely
 * runs in airplane mode and the caption under the phone points at it. Since
 * 61e9 the caption no longer says "named, tagged": automatic naming moved into
 * the paid cloud cleanup, so it is not something the airplane can vouch for.
 * The title and tag still shown here stay honest because the app lets the
 * owner type both by hand (`edit-recording`'s title field and tag input) —
 * they read as the student's own edits, not as something the free app
 * generated. The linked row also shows the note's title
 * before its date, which the app renders date-only (inside a note the link's
 * own alias already carries the title). The two small affordances — add-link
 * and unlink — are drawn as a plus and a circled cross where the app draws MDI
 * `link-plus` and `close-circle-outline`, which are mush at this scale.
 *
 * The example note is the student thread the whole page tells: a psych lecture
 * thought that linked itself to a reading note, with a third note the app
 * noticed on its own.
 *
 * ## Type and colour
 *
 * The serif and mono stacks are local constants mirroring `paper-theme.ts` and
 * `note-artifact`'s precedent: the transcript sits at a size the typescale has
 * no serif step for, and the durations render in the same system mono the app
 * uses. Colours are all audited roles — `tertiary` for the live airplane and
 * for Related (the app's own hue for a suggestion), `primary` for the action
 * pills and the add-link control with `onPrimary` text, `error` for the unlink
 * cross, elevation fills for the frame and the Related card — so
 * `contrast.test.ts` already covers every pairing.
 *
 * Decorative throughout: the caption below carries the meaning, so the whole
 * phone is hidden from assistive technology rather than read out as a stack of
 * twenty fragments.
 */

import { StyleSheet, Text as RawText, View } from "react-native";
import { Text } from "react-native-paper";
import { LinkGlyph, SuggestionGlyph } from "@/components/icons";
import {
  MONO_STACK,
  SERIF_STACK,
  type AppTheme,
  useAppTheme,
} from "@/theme/paper-theme";
import { iconSize, radius, spacing } from "@/theme/tokens";

// Bespoke device geometry: the outer shell's rounding, the screen's tighter
// rounding inside it, and the screen's own horizontal inset (also unwound by
// the transcript block's full-bleed negative margin).
const FRAME_RADIUS = 44;
const SCREEN_RADIUS = 34;
const SCREEN_INSET = spacing.md + 2;

const NOTE_TITLE = "Psych — spacing effect";
const NOTE_META = "Aug 12, 2026 at 4:12 PM";
const TRANSCRIPT =
  "Professor said spacing out practice beats cramming, every time. Try it for the stats midterm — short reviews all week instead of one long night.";
const LINKED_TITLE = "Make It Stick — ch. 3 · Aug 10";
const LINKED_DURATION = "00:12";

const RELATED_CAPTION = "Similar notes — not linked yet.";
const RELATED_TITLE = "Flashcards actually work";
const RELATED_DURATION = "00:41";

const CAPTION =
  "What you said on top, the notes that go with it underneath. Written down and filed before you put the phone down. That little airplane? On the whole time.";

/** A tag chip as the app draws it: hairline outline, squared corners. */
function TagChip({ label, theme }: { label: string; theme: AppTheme }) {
  return (
    <View style={[styles.tagChip, { borderColor: theme.colors.outline }]}>
      <RawText style={[styles.tagChipText, { color: theme.colors.onSurface }]}>
        {label}
      </RawText>
    </View>
  );
}

/** The teal action pill ("Listen", "Play") with its play triangle. */
function ActionPill({ label, theme }: { label: string; theme: AppTheme }) {
  return (
    <View style={[styles.pill, { backgroundColor: theme.colors.primary }]}>
      <svg
        width={10}
        height={10}
        viewBox="0 0 24 24"
        fill={theme.colors.onPrimary}
        aria-hidden
        focusable="false"
        style={{ display: "block", flexShrink: 0 }}
      >
        <path d="M7 4l13 8-13 8V4z" />
      </svg>
      <RawText style={[styles.pillText, { color: theme.colors.onPrimary }]}>
        {label}
      </RawText>
    </View>
  );
}

/**
 * The row-level affordances the app puts on this screen: add a link, drop a
 * link, promote a suggestion. Stroked at the same weight as `Glyph` so they sit
 * beside `LinkGlyph` without looking lighter, and drawn here rather than
 * exported because they are 11 px control marks inside a mock, not part of the
 * site's icon set.
 */
function Affordance({
  shape,
  color,
  size = 11,
}: {
  shape: "plus" | "unlink";
  color: string;
  size?: number;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2.25}
      strokeLinecap="round"
      aria-hidden
      focusable="false"
      style={{ display: "block", flexShrink: 0 }}
    >
      {shape === "plus" ? (
        <path d="M12 5v14M5 12h14" />
      ) : (
        <>
          <circle cx="12" cy="12" r="8.5" />
          <path d="M9 9l6 6M15 9l-6 6" />
        </>
      )}
    </svg>
  );
}

export function PhoneMock() {
  const theme = useAppTheme();

  const quiet = { color: theme.colors.onSurfaceVariant };

  return (
    <View style={styles.stage}>
      <View
        aria-hidden
        style={[
          styles.frame,
          {
            backgroundColor: theme.colors.elevation.level1,
            borderColor: theme.colors.outline,
          },
        ]}
      >
        <View
          style={[
            styles.screen,
            {
              backgroundColor: theme.colors.background,
              borderColor: theme.colors.outlineVariant,
            },
          ]}
        >
          <View style={styles.statusBar}>
            <RawText style={[styles.statusText, quiet]}>9:41</RawText>
            <RawText style={[styles.plane, { color: theme.colors.tertiary }]}>
              ✈
            </RawText>
          </View>

          <View style={styles.navRow}>
            <View
              style={[
                styles.navPill,
                { backgroundColor: theme.colors.elevation.level1 },
              ]}
            >
              <RawText
                style={[styles.navPillText, { color: theme.colors.onSurface }]}
              >
                ‹ Recordings
              </RawText>
            </View>
            <RawText
              style={[styles.navTitle, { color: theme.colors.onSurface }]}
            >
              Recording
            </RawText>
            <View
              style={[
                styles.navPill,
                { backgroundColor: theme.colors.elevation.level1 },
              ]}
            >
              <RawText
                style={[styles.navPillText, { color: theme.colors.onSurface }]}
              >
                ✎
              </RawText>
            </View>
          </View>

          <View>
            <RawText
              style={[styles.noteTitle, { color: theme.colors.onSurface }]}
            >
              {NOTE_TITLE}
            </RawText>
            <RawText style={[styles.noteMeta, quiet]}>{NOTE_META}</RawText>
          </View>

          <View style={styles.tagBlock}>
            <RawText style={[styles.sectionLabel, quiet]}>Tags</RawText>
            <TagChip label="school" theme={theme} />
          </View>

          <View style={styles.rowBetween}>
            <RawText style={[styles.sectionLabel, quiet]}>
              Transcription
            </RawText>
            <ActionPill label="Listen" theme={theme} />
          </View>

          <RawText
            style={[styles.transcript, { color: theme.colors.onSurface }]}
          >
            {TRANSCRIPT}
          </RawText>

          {/* The header carries the app's add-link control, so linking is
              reachable from the note itself; each row carries its own unlink. */}
          <View style={styles.linkedBlock}>
            <View style={styles.rowBetween}>
              <RawText style={[styles.sectionLabel, quiet]}>
                Linked Recordings
              </RawText>
              <Affordance shape="plus" color={theme.colors.primary} />
            </View>
            <View
              style={[
                styles.rule,
                { borderTopColor: theme.colors.outlineVariant },
              ]}
            />
            <View style={styles.linkedRow}>
              <View style={styles.linkedGlyph}>
                <LinkGlyph size={iconSize.sm} color={theme.colors.primary} />
              </View>
              <View style={styles.linkedBody}>
                <RawText
                  style={[
                    styles.linkedTitle,
                    { color: theme.colors.onSurface },
                  ]}
                >
                  {LINKED_TITLE}
                </RawText>
                <RawText style={[styles.linkedDuration, quiet]}>
                  {LINKED_DURATION}
                </RawText>
                <TagChip label="reading" theme={theme} />
              </View>
              <View style={styles.linkedGlyph}>
                <Affordance shape="unlink" color={theme.colors.error} />
              </View>
            </View>
          </View>

          {/* Related: derived suggestions the vault does not yet record, so the
              app sets them apart from the links above — its own gold hue, the
              suggestion glyph instead of the link glyph, and a raised card
              instead of the flat divider-and-rows. The trailing plus promotes a
              suggestion into an ordinary link. This is the mechanism the page's
              self-linking sections describe, so the phone has to show it. */}
          <View style={styles.relatedBlock}>
            <View style={styles.relatedHeader}>
              <SuggestionGlyph
                size={iconSize.sm}
                color={theme.colors.tertiary}
              />
              <RawText
                style={[styles.sectionLabel, { color: theme.colors.tertiary }]}
              >
                Related
              </RawText>
            </View>
            <RawText style={[styles.relatedCaption, quiet]}>
              {RELATED_CAPTION}
            </RawText>
            <View
              style={[
                styles.relatedCard,
                { backgroundColor: theme.colors.elevation.level2 },
              ]}
            >
              <View style={styles.relatedRow}>
                <SuggestionGlyph
                  size={iconSize.sm}
                  color={theme.colors.tertiary}
                />
                <View style={styles.relatedBody}>
                  <RawText
                    style={[
                      styles.linkedTitle,
                      { color: theme.colors.onSurface },
                    ]}
                  >
                    {RELATED_TITLE}
                  </RawText>
                  <RawText style={[styles.linkedDuration, quiet]}>
                    {RELATED_DURATION}
                  </RawText>
                </View>
                <Affordance shape="plus" color={theme.colors.primary} />
              </View>
            </View>
          </View>

          {/* The app's playback bar: a progress track, the two times beneath it,
              and the action below them — not one row with the button wedged
              between the times. */}
          <View
            style={[
              styles.playBar,
              { backgroundColor: theme.colors.elevation.level1 },
            ]}
          >
            {/* At rest, which is what the "Play" pill below means: the app's
                ProgressBar sits empty until playback starts. */}
            <View
              style={[
                styles.progressTrack,
                { backgroundColor: theme.colors.surfaceVariant },
              ]}
            />
            <View style={styles.playTimes}>
              <RawText style={[styles.playTime, quiet]}>00:00</RawText>
              <RawText style={[styles.playTime, quiet]}>00:09</RawText>
            </View>
            <ActionPill label="Play" theme={theme} />
          </View>
        </View>
      </View>

      <Text variant="bodyMedium" style={[styles.caption, quiet]}>
        {CAPTION}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  stage: {
    alignItems: "center",
    gap: spacing.md,
    width: "100%",
  },
  frame: {
    width: 340,
    maxWidth: "100%",
    borderWidth: 1.5,
    borderRadius: FRAME_RADIUS,
    padding: spacing.sm,
  },
  screen: {
    borderWidth: 1,
    borderRadius: SCREEN_RADIUS,
    paddingTop: spacing.md,
    paddingHorizontal: SCREEN_INSET,
    gap: spacing.sm,
    overflow: "hidden",
  },
  statusBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  statusText: {
    fontSize: 12,
    fontWeight: "600",
  },
  plane: {
    fontSize: 13,
  },
  navRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: spacing.xs,
  },
  navPill: {
    borderRadius: radius.full,
    paddingVertical: 6,
    paddingHorizontal: spacing.sm,
  },
  navPillText: {
    fontSize: 12.5,
    fontWeight: "500",
  },
  navTitle: {
    fontFamily: SERIF_STACK,
    fontSize: 15,
    fontWeight: "700",
  },
  noteTitle: {
    fontFamily: SERIF_STACK,
    fontSize: 24,
    fontWeight: "700",
    lineHeight: 29,
  },
  noteMeta: {
    fontSize: 12,
    marginTop: 3,
  },
  tagBlock: {
    gap: 6,
    alignItems: "flex-start",
  },
  sectionLabel: {
    fontSize: 12.5,
  },
  tagChip: {
    borderWidth: 1,
    borderRadius: 10,
    paddingVertical: 4,
    paddingHorizontal: spacing.sm,
    alignSelf: "flex-start",
  },
  tagChipText: {
    fontSize: 12.5,
  },
  rowBetween: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: spacing.xs,
  },
  pill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    borderRadius: radius.full,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
  },
  pillText: {
    fontSize: 13,
    fontWeight: "600",
  },
  transcript: {
    fontFamily: SERIF_STACK,
    fontSize: 15.5,
    lineHeight: 23,
  },
  linkedBlock: {
    gap: spacing.xs,
  },
  rule: {
    borderTopWidth: 1,
  },
  linkedRow: {
    flexDirection: "row",
    gap: spacing.sm,
    alignItems: "flex-start",
    paddingTop: spacing.xxs,
  },
  linkedGlyph: {
    marginTop: 4,
  },
  linkedBody: {
    flex: 1,
    gap: 4,
    alignItems: "flex-start",
  },
  linkedTitle: {
    fontSize: 13.5,
    fontWeight: "600",
  },
  linkedDuration: {
    fontFamily: MONO_STACK,
    fontSize: 12,
  },
  // Tighter than the link block above it: heading, caption and card read as one
  // unit, which is part of what sets Related apart on the real screen.
  relatedBlock: {
    gap: spacing.xxs,
  },
  relatedHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  relatedCaption: {
    fontSize: 11.5,
  },
  relatedCard: {
    borderRadius: radius.sm,
    marginTop: 2,
    overflow: "hidden",
  },
  relatedRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  relatedBody: {
    flex: 1,
    gap: 3,
    alignItems: "flex-start",
  },
  // Bleeds to the screen edges the way the app's bar does: the negative
  // horizontal margins undo the screen's own inset.
  playBar: {
    alignItems: "center",
    gap: 6,
    marginHorizontal: -SCREEN_INSET,
    marginTop: spacing.xxs,
    paddingHorizontal: SCREEN_INSET,
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
  },
  progressTrack: {
    width: "100%",
    height: 4,
    borderRadius: radius.full,
  },
  playTimes: {
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  playTime: {
    fontFamily: MONO_STACK,
    fontSize: 12.5,
  },
  caption: {
    maxWidth: 440,
    textAlign: "center",
  },
});
